/**
 * useAuthProviderForm Hook
 * Manages form state for adding/editing authentication providers
 */

import { useState, useCallback } from 'react';
import type {
  AuthProviderConfig,
  AuthProviderForm,
} from '../../components/system/AuthProviderSettings/types';

interface UseAuthProviderFormReturn {
  form: AuthProviderForm;
  dialogOpen: boolean;
  editingProvider: AuthProviderConfig | null;
  error: string | null;
  handleOpenAdd: () => void;
  handleOpenEdit: (provider: AuthProviderConfig) => void;
  handleCloseDialog: () => void;
  handleFieldChange: (key: string, value: string | boolean) => void;
  handleProviderTypeChange: (type: string) => void;
  resetForm: () => void;
  setError: (error: string | null) => void;
}

/**
 * Get default configuration for Entra provider
 */
const getDefaultEntraConfig = () => {
  const baseUrl =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.PROD
      ? `${window.location.protocol}//${window.location.host}`
      : 'http://localhost:8080');

  return {
    redirect_uri: `${baseUrl}/api/auth/callback/entra`,
    sync_groups: true,
    group_claim: 'groups',
  };
};

/**
 * Hook to manage authentication provider form state
 */
export const useAuthProviderForm = (): UseAuthProviderFormReturn => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<AuthProviderConfig | null>(
    null
  );
  const [form, setForm] = useState<AuthProviderForm>({
    provider_type: 'entra',
    config: {},
  });
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setForm({
      provider_type: 'entra',
      config: {},
    });
    setError(null);
  }, []);

  const handleOpenAdd = useCallback(() => {
    setEditingProvider(null);

    // Pre-populate Entra config with defaults
    const defaultConfig = getDefaultEntraConfig();

    setForm({ provider_type: 'entra', config: defaultConfig });
    setError(null);
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback((provider: AuthProviderConfig) => {
    setEditingProvider(provider);
    setForm({
      provider_type: provider.provider_type,
      config: { ...provider.config },
    });
    setError(null);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setError(null);
  }, []);

  const handleFieldChange = useCallback((key: string, value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      config: { ...prev.config, [key]: value },
    }));
  }, []);

  const handleProviderTypeChange = useCallback((type: string) => {
    // Pre-populate config with defaults when switching provider types
    const defaultConfig = type === 'entra' ? getDefaultEntraConfig() : {};

    setForm((prev) => ({ ...prev, provider_type: type, config: defaultConfig }));
  }, []);

  return {
    form,
    dialogOpen,
    editingProvider,
    error,
    handleOpenAdd,
    handleOpenEdit,
    handleCloseDialog,
    handleFieldChange,
    handleProviderTypeChange,
    resetForm,
    setError,
  };
};
